// PostHog analytics wrapper.
//
// Purpose: capture the TOP of the funnel the membership DB can't see — anonymous
// visitors who land on the homepage (or any page) and never create an account —
// plus conversion events (registration started, email verified, login).
//
// Safe by design: if REACT_APP_POSTHOG_KEY is not set (e.g. local dev without a
// key), every function here becomes a no-op so nothing breaks. All calls are
// wrapped in try/catch so analytics can never crash the app.

import posthog from 'posthog-js';

let enabled = false;

// localStorage key recording the visitor's session-replay consent choice.
const CONSENT_KEY = 'ph_replay_consent';

export function initAnalytics() {
  const key = process.env.REACT_APP_POSTHOG_KEY;
  const host = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

  if (!key) {
    // No key configured -> stay a no-op (local dev / key not provisioned yet).
    return;
  }

  try {
    posthog.init(key, {
      api_host: host,
      // We fire $pageview ourselves on each React Router change (SPA), so disable
      // the automatic one which only fires on hard loads.
      capture_pageview: false,
      // Respect Do Not Track; keep it privacy-friendly.
      respect_dnt: true,
      persistence: 'localStorage+cookie',
      autocapture: true,
      // Session Replay: start DISABLED. Recording only begins after the visitor
      // grants consent (see grantConsent()). Even once started, mask everything
      // so passwords / emails / financial data are never captured (GDPR-safe).
      disable_session_recording: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
      },
    });
    enabled = true;
    // If the user previously consented (persisted), resume recording now.
    try {
      if (localStorage.getItem(CONSENT_KEY) === 'granted') {
        posthog.startSessionRecording();
      }
    } catch (_) { /* no-op */ }
  } catch (e) {
    // Never let analytics init break the app.
    // eslint-disable-next-line no-console
    console.warn('PostHog init failed (analytics disabled):', e);
    enabled = false;
  }
}

export function capturePageview(path) {
  if (!enabled) return;
  try {
    posthog.capture('$pageview', { $current_url: window.location.href, path });
  } catch (_) { /* no-op */ }
}

export function captureEvent(event, props) {
  if (!enabled) return;
  try {
    posthog.capture(event, props || {});
  } catch (_) { /* no-op */ }
}

// Tie subsequent events to a known member once they log in. Stitches the prior
// anonymous session (homepage visits etc.) to this identified user.
export function identifyUser(user) {
  if (!enabled || !user) return;
  try {
    posthog.identify(String(user.id ?? user.email), {
      email: user.email,
      name: user.name,
      subscription: user.subscription,
      is_admin: user.isAdmin === true,
    });
  } catch (_) { /* no-op */ }
}

// Clear identity on logout so the next visitor isn't attributed to this user.
export function resetUser() {
  if (!enabled) return;
  try {
    posthog.reset();
  } catch (_) { /* no-op */ }
}

export function isAnalyticsEnabled() {
  return enabled;
}

// --- Session Replay consent gate ---
// Replay stays OFF until the visitor explicitly grants consent (cookie banner).
// Basic pageview/funnel analytics (no screen recording) run regardless; only the
// screen-recording layer is gated here.

export function getReplayConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY); // 'granted' | 'denied' | null
  } catch (_) {
    return null;
  }
}

export function grantConsent() {
  try {
    localStorage.setItem(CONSENT_KEY, 'granted');
    if (enabled) posthog.startSessionRecording();
  } catch (_) { /* no-op */ }
}

export function revokeConsent() {
  try {
    localStorage.setItem(CONSENT_KEY, 'denied');
    if (enabled) posthog.stopSessionRecording();
  } catch (_) { /* no-op */ }
}
